import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Plus } from 'lucide-react';
import { useCreatePaymentMethod, useUpdatePaymentMethod } from '@/hooks/usePaymentMethods';
import { PAYMENT_METHODS } from '@/utils/constants';

interface EditPaymentMethodDialogProps {
  authorId: string;
  paymentMethod?: any;
  isEdit?: boolean;
}

const EditPaymentMethodDialog: React.FC<EditPaymentMethodDialogProps> = ({ 
  authorId, 
  paymentMethod, 
  isEdit = false 
}) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(paymentMethod?.type || 'paypal');
  
  // Get threshold from constants based on type, with automatic setting for check
  const getThresholdForType = (paymentType: string) => {
    const method = Object.values(PAYMENT_METHODS).find(m => m.type === paymentType);
    return method ? method.threshold : 75;
  };
  
  const [threshold, setThreshold] = useState(paymentMethod?.threshold || getThresholdForType(paymentMethod?.type || 'paypal'));
  
  // Initialize payment details correctly from the payment_details array
  const paymentDetails = paymentMethod?.payment_details?.[0] || {};
  const [paypalEmail, setPaypalEmail] = useState(paymentDetails?.paypal_email || '');
  const [bankName, setBankName] = useState(paymentDetails?.bank_name || '');
  const [accountNumber, setAccountNumber] = useState(paymentDetails?.account_number || '');
  const [routingNumber, setRoutingNumber] = useState(paymentDetails?.routing_number || '');
  const [swift, setSwift] = useState(paymentDetails?.swift || '');
  const [address, setAddress] = useState(paymentDetails?.address || '');

  const createPaymentMethod = useCreatePaymentMethod();
  const updatePaymentMethod = useUpdatePaymentMethod();

  // Reset form when payment method type changes and auto-set threshold
  React.useEffect(() => {
    if (paymentMethod && isEdit) {
      setType(paymentMethod.type);
      // Use threshold from constants for consistency
      setThreshold(getThresholdForType(paymentMethod.type));
      const details = paymentMethod.payment_details?.[0] || {};
      setPaypalEmail(details.paypal_email || '');
      setBankName(details.bank_name || '');
      setAccountNumber(details.account_number || '');
      setRoutingNumber(details.routing_number || '');
      setSwift(details.swift || '');
      setAddress(details.address || '');
    }
  }, [paymentMethod, isEdit, open]);

  // Auto-update threshold when type changes
  React.useEffect(() => {
    setThreshold(getThresholdForType(type));
  }, [type]);

  const handleSave = async () => {
    try {
      const details: any = {};
      
      if (type === 'paypal') {
        details.paypal_email = paypalEmail;
      } else if (type === 'bank_transfer_us') {
        details.bank_name = bankName;
        details.account_number = accountNumber;
        details.routing_number = routingNumber;
      } else if (type === 'bank_transfer_international') {
        details.bank_name = bankName;
        details.account_number = accountNumber;
        details.swift = swift;
        details.address = address;
      } else if (type === 'check') {
        details.address = address;
      }

      if (isEdit && paymentMethod) {
        await updatePaymentMethod.mutateAsync({
          methodId: paymentMethod.id,
          authorId,
          updates: { type, threshold },
          details
        });
      } else {
        await createPaymentMethod.mutateAsync({
          authorId,
          type,
          threshold,
          details
        });
      }
      
      setOpen(false);
    } catch (error) {
      console.error('Error saving payment method:', error);
    }
  };

  const renderPaymentFields = () => {
    switch (type) {
      case 'paypal':
        return (
          <div className="grid gap-2">
            <Label htmlFor="paypal-email">PayPal Email</Label>
            <Input
              id="paypal-email"
              type="email"
              value={paypalEmail}
              onChange={(e) => setPaypalEmail(e.target.value)}
              placeholder="Enter PayPal email"
            />
          </div>
        );
      
      case 'bank_transfer_us':
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="bank-name">Bank Name</Label>
              <Input
                id="bank-name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Enter bank name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account-number">Account Number</Label>
              <Input
                id="account-number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Enter account number"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="routing-number">Routing Number</Label>
              <Input
                id="routing-number"
                value={routingNumber}
                onChange={(e) => setRoutingNumber(e.target.value)}
                placeholder="Enter routing number"
              />
            </div>
          </>
        );
      
      case 'bank_transfer_international':
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="bank-name">Bank Name</Label>
              <Input
                id="bank-name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Enter bank name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account-number">Account Number</Label>
              <Input
                id="account-number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Enter account number"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="swift">SWIFT Code</Label>
              <Input
                id="swift"
                value={swift}
                onChange={(e) => setSwift(e.target.value)}
                placeholder="Enter SWIFT code"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address">Bank Address</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter bank address"
              />
            </div>
          </>
        );
      
      case 'check':
        return (
          <div className="grid gap-2">
            <Label htmlFor="address">Mailing Address</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter mailing address"
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={isEdit ? "ghost" : "default"} size="sm">
          {isEdit ? (
            <>
              <CreditCard className="h-4 w-4 mr-1" />
              Edit Payment
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add Payment Method
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Payment Method' : 'Add Payment Method'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="type">Payment Method Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="bank_transfer_us">US Bank Transfer</SelectItem>
                <SelectItem value="bank_transfer_international">International Bank Transfer</SelectItem>
                <SelectItem value="check">Check</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="threshold">Payment Threshold ($)</Label>
            <Input
              id="threshold"
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              placeholder="Enter minimum amount for payment"
              disabled={type === 'check'} // Disable for check since it's fixed at $75
            />
            {type === 'check' && (
              <p className="text-xs text-muted-foreground">
                Check payments have a fixed threshold of $75
              </p>
            )}
          </div>
          
          {renderPaymentFields()}
        </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={createPaymentMethod.isPending || updatePaymentMethod.isPending}
          >
            {(createPaymentMethod.isPending || updatePaymentMethod.isPending) ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditPaymentMethodDialog;
